import React, { useState, useEffect } from 'react';
import {
  Box, Paper, TextField, Button, Typography, Grid, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, MenuItem,
  Card, CardContent, Divider, useTheme, Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack,
  Lock as LockIcon,
  ContentCopy as DuplicateIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import axios from '../../../axios';
import chartOfAccountsApi from '../../../api/chartOfAccountsApi';
import AccountSelect from '../../../components/AccountSelect';
import ImportJournalEntryDialog from './ImportJournalEntryDialog';
import JournalEntryPrint from './JournalEntryPrint';
import { format } from 'date-fns';

const JournalEntryForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  const branchId = parseInt(localStorage.getItem('branchId'), 10);
  const isEdit = !!id;
  const isView = location.pathname.includes('/view');
  const initialType = parseInt(searchParams.get('type')) || 1;

  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    referenceNumber: '',
    entryType: initialType,
    description: '',
    branchId: branchId,
    companyId: companyId,
    entryNumber: '',
  });

  const [lines, setLines] = useState([
    { id: 1, accountId: null, description: '', debit: 0, credit: 0 },
    { id: 2, accountId: null, description: '', debit: 0, credit: 0 }
  ]);

  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [accountMap, setAccountMap] = useState({});
  const printRef = React.useRef();

  useEffect(() => {
    chartOfAccountsApi.getAll().then(res => {
      const map = {};
      res.data?.forEach(acc => {
        map[acc.id] = `${acc.code} - ${acc.nameAr}`;
      });
      setAccountMap(map);
    }).catch(err => console.error('Error fetching accounts for print:', err));
  }, []);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Journal_Entry_${formData.entryNumber || 'New'}`,
  });

  const handleImportSuccess = (importedLines) => {
    const newLines = importedLines.map((l, index) => ({
      id: Date.now() + index,
      accountId: l.accountId,
      description: l.description || '',
      debit: l.debit,
      credit: l.credit
    }));

    const isInitial = lines.length === 2 && lines.every(l => !l.accountId && !l.debit && !l.credit);

    if (isInitial) {
      setLines(newLines);
    } else {
      setLines([...lines, ...newLines]);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchEntry();
    } else {
      fetchNextNumber();
    }
  }, [id, formData.entryType]);

  const fetchNextNumber = async () => {
    try {
      const response = await axios.get('/JournalEntries/next-number', {
        params: {
          branchId: formData.branchId,
          entryType: formData.entryType
        }
      });
      setFormData(prev => ({ ...prev, entryNumber: response.data.nextNumber }));
    } catch (error) {
      console.error('Failed to fetch next number', error);
    }
  };

  const fetchEntry = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/JournalEntries/${id}`);
      const entry = response.data;
      setFormData({
        date: format(new Date(entry.date), 'yyyy-MM-dd'),
        referenceNumber: entry.referenceNumber || '',
        entryType: entry.entryType,
        description: entry.description,
        branchId: entry.branchId,
        entryNumber: entry.entryNumber
      });
      setLines(entry.lines.map((l, index) => ({
        id: l.id || index,
        accountId: l.accountId,
        description: l.description,
        debit: l.debit,
        credit: l.credit
      })));
    } catch (error) {
      console.error('Error fetching entry:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...lines];
    newLines[index][field] = value;

    if (field === 'debit' && Number(value) > 0) newLines[index].credit = 0;
    if (field === 'credit' && Number(value) > 0) newLines[index].debit = 0;

    if (field === 'accountId' && value && index > 0) {
      let currentDebit = 0;
      let currentCredit = 0;

      newLines.forEach((line, idx) => {
        if (idx !== index) {
          currentDebit += Number(line.debit || 0);
          currentCredit += Number(line.credit || 0);
        }
      });

      const diff = currentDebit - currentCredit;

      if (diff > 0 && !newLines[index].debit && !newLines[index].credit) {
        newLines[index].credit = diff;
        newLines[index].debit = 0;
      } else if (diff < 0 && !newLines[index].debit && !newLines[index].credit) {
        newLines[index].debit = Math.abs(diff);
        newLines[index].credit = 0;
      }
    }

    setLines(newLines);
  };

  const handleKeyDown = (e, index, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      if (field === 'credit' && index === lines.length - 1) {
        addLine();
      } else {
        const formElements = e.target.form.elements;
        const indexInForm = Array.prototype.indexOf.call(formElements, e.target);
        if (indexInForm > -1 && indexInForm < formElements.length - 1) {
          const nextElement = formElements[indexInForm + 1];
          if (nextElement && !nextElement.disabled) {
            nextElement.focus();
            if (nextElement.select) nextElement.select();
          }
        }
      }
    }
  };

  const handleFocus = (event) => {
    event.target.select();
  };

  const addLine = () => {
    setLines([...lines, { id: Date.now(), accountId: null, description: '', debit: 0, credit: 0 }]);
  };

  const removeLine = (index) => {
    if (lines.length <= 2) return;
    setLines(lines.filter((_, i) => i !== index));
  };

  const getTotalDebit = () => lines.reduce((sum, line) => sum + Number(line.debit || 0), 0);
  const getTotalCredit = () => lines.reduce((sum, line) => sum + Number(line.credit || 0), 0);
  const getDifference = () => getTotalDebit() - getTotalCredit();

  const handleDuplicate = async () => {
    try {
      const response = await axios.get('/JournalEntries/next-number', {
        params: {
          branchId: formData.branchId,
          entryType: formData.entryType
        }
      });

      setFormData(prev => ({
        ...prev,
        entryNumber: response.data.nextNumber,
        date: format(new Date(), 'yyyy-MM-dd'),
        referenceNumber: '',
        description: prev.description ? `${prev.description} (نسخة)` : ''
      }));

      setLines(prevLines => prevLines.map((l, idx) => ({
        ...l,
        id: Date.now() + idx
      })));

      navigate('/journal-entries/new', { replace: true });
    } catch (error) {
      console.error('Failed to prepare duplication', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (Math.abs(getDifference()) > 0.01) {
      alert(t('entriesNotBalanced'));
      return;
    }

    if (lines.some(l => !l.accountId)) {
      alert(t('allLinesMustHaveAccount'));
      return;
    }

    const finalDescription = formData.description || `${t(entryTypes.find(item => item.value === formData.entryType)?.label)} #${formData.entryNumber}`;

    try {
      setLoading(true);
      const payload = {
        branchId: formData.branchId,
        date: formData.date,
        entryType: formData.entryType,
        description: finalDescription,
        referenceNumber: formData.referenceNumber,
        lines: lines.map(l => ({
          accountId: l.accountId,
          description: l.description || finalDescription,
          debit: Number(l.debit),
          credit: Number(l.credit),
          costCenterId: l.costCenterId || null
        }))
      };

      if (isEdit) {
        await axios.put(`/JournalEntries/${id}`, payload);
      } else {
        await axios.post('/JournalEntries', payload);
      }
      navigate('/journal-entries');
    } catch (error) {
      console.error('Error saving entry:', error);
      alert(error.response?.data?.title || error.response?.data || 'Error saving');
    } finally {
      setLoading(false);
    }
  };

  const entryTypes = [
    { value: 1, label: t('dailyEntry') },
    { value: 2, label: t('openingEntry') },
    { value: 3, label: t('salesEntry') },
    { value: 4, label: t('salesReturnEntry') },
    { value: 5, label: t('purchaseEntry') },
    { value: 6, label: t('purchaseReturnEntry') },
    { value: 7, label: t('paymentVoucher') },
    { value: 8, label: t('receiptVoucher') },
    { value: 9, label: t('transferEntry') },
    { value: 10, label: t('adjustmentEntry') },
    { value: 11, label: t('depreciationEntry') },
    { value: 12, label: t('payrollEntry') },
    { value: 13, label: t('closingEntry') }
  ];

  const fieldSx = {
    '& .MuiInputLabel-root': { color: 'text.secondary', fontWeight: 600 },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
    '& .MuiOutlinedInput-root': {
      bgcolor: 'background.paper',
      '& fieldset': { borderColor: 'divider' },
      '&:hover fieldset': { borderColor: 'primary.main' },
      '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 2 },
      '& input, & textarea': { color: 'text.primary', WebkitTextFillColor: 'text.primary' },
      '&.Mui-disabled': { bgcolor: 'action.hover' },
      '&.Mui-disabled input': { WebkitTextFillColor: 'text.secondary' },
    },
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: '100%', mx: 'auto' }} dir="rtl">
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          px: { xs: 1, sm: 2 },
          py: 1.5,
          mb: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/journal-entries')} size="small" sx={{ color: 'primary.main' }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', fontSize: { xs: '1rem', sm: '1.2rem' } }}>
            {isView ? t('viewEntry') : (isEdit ? t('editEntry') : t('newEntry'))}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {(isEdit || isView) && [1, 2].includes(Number(formData.entryType)) && (
            <Button variant="outlined" color="secondary" size="small" startIcon={<DuplicateIcon />} onClick={handleDuplicate}>
              {t('duplicateEntry')}
            </Button>
          )}
          {!isView && (
            <Button variant="outlined" size="small" onClick={() => setImportOpen(true)}>
              📥 استيراد من Excel
            </Button>
          )}
          <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={handlePrint}>
            {t('PRINT')}
          </Button>
        </Box>
      </Box>

      <div style={{ display: 'none' }}>
        <JournalEntryPrint
          ref={printRef}
          data={{
            ...formData,
            lines: lines.map(l => ({ ...l, accountName: accountMap[l.accountId] || '' }))
          }}
          entryTypes={entryTypes}
        />
      </div>

      <form onSubmit={handleSubmit}>
        <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, borderColor: 'divider', boxShadow: 'none' }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  sx={fieldSx}
                  size="small"
                  fullWidth
                  label={t('entryNumber')}
                  value={isEdit ? formData.entryNumber : t('autoGenerated')}
                  disabled
                  InputProps={{
                    startAdornment: <LockIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  sx={fieldSx}
                  size="small"
                  fullWidth
                  label={t('date')}
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  disabled={isView}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  sx={fieldSx}
                  size="small"
                  fullWidth
                  select
                  label={t('entryType')}
                  value={formData.entryType}
                  onChange={(e) => setFormData({ ...formData, entryType: Number(e.target.value) })}
                  required
                  disabled
                >
                  {entryTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  sx={fieldSx}
                  size="small"
                  fullWidth
                  label={t('referenceNumber')}
                  value={formData.referenceNumber}
                  onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
                  placeholder={t('optional')}
                  disabled={isView}
                />
              </Grid>

              {/* Description intentionally placed last and styled with clear contrast. */}
              <Grid item xs={12} md={4}>
                <TextField
                  sx={{
                    ...fieldSx,
                    '& .MuiInputLabel-root': { color: 'text.primary', fontWeight: 700 },
                    '& .MuiOutlinedInput-root': {
                      ...fieldSx['& .MuiOutlinedInput-root'],
                      '& fieldset': { borderColor: 'primary.main', borderWidth: 1.5 },
                      '&:hover fieldset': { borderColor: 'primary.dark' },
                      '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 2 },
                    },
                  }}
                  size="small"
                  fullWidth
                  label={t('description')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  minRows={1}
                  placeholder={t('enterDescription')}
                  disabled={isView}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, borderColor: 'divider', boxShadow: 'none', overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell width="32%" sx={{ color: 'primary.contrastText', fontWeight: 700, whiteSpace: 'nowrap' }}>{t('account')}</TableCell>
                  <TableCell width="12%" sx={{ color: 'primary.contrastText', fontWeight: 700, whiteSpace: 'nowrap' }}>{t('debit')}</TableCell>
                  <TableCell width="12%" sx={{ color: 'primary.contrastText', fontWeight: 700, whiteSpace: 'nowrap' }}>{t('credit')}</TableCell>
                  <TableCell width="24%" sx={{ color: 'primary.contrastText', fontWeight: 700, whiteSpace: 'nowrap' }}>{t('description')}</TableCell>
                  <TableCell width="5%" />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, index) => (
                  <TableRow key={line.id} hover>
                    {/* RTL order: account, debit, credit, description, delete */}
                    <TableCell sx={{ p: 1 }}>
                      <AccountSelect
                        value={line.accountId}
                        onChange={(val) => handleLineChange(index, 'accountId', val)}
                        required
                        size="small"
                        disabled={isView}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 1 }}>
                      <TextField
                        sx={fieldSx}
                        fullWidth
                        type="number"
                        size="small"
                        value={line.debit}
                        onChange={(e) => handleLineChange(index, 'debit', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index, 'debit')}
                        onFocus={handleFocus}
                        inputProps={{ min: 0, step: 0.01 }}
                        disabled={isView}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 1 }}>
                      <TextField
                        sx={fieldSx}
                        fullWidth
                        type="number"
                        size="small"
                        value={line.credit}
                        onChange={(e) => handleLineChange(index, 'credit', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index, 'credit')}
                        onFocus={handleFocus}
                        inputProps={{ min: 0, step: 0.01 }}
                        disabled={isView}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 1 }}>
                      <TextField
                        sx={{
                          ...fieldSx,
                          '& .MuiOutlinedInput-root fieldset': { borderColor: 'primary.main' },
                          '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'primary.dark' },
                        }}
                        fullWidth
                        size="small"
                        value={line.description}
                        onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index, 'description')}
                        placeholder={t('lineDescription')}
                        disabled={isView}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ p: 1 }}>
                      <IconButton
                        onClick={() => removeLine(index)}
                        color="error"
                        size="small"
                        disabled={lines.length <= 2 || isView}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={5} sx={{ p: 1 }}>
                    {!isView && (
                      <Button startIcon={<AddIcon />} onClick={addLine} size="small">
                        {t('addLine')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Paper
          variant="outlined"
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: 2,
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, gap: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">{t('totalDebit')}</Typography>
                <Typography variant="subtitle1" fontWeight={800} color="text.primary">{getTotalDebit().toLocaleString()}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, gap: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">{t('totalCredit')}</Typography>
                <Typography variant="subtitle1" fontWeight={800} color="text.primary">{getTotalCredit().toLocaleString()}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, gap: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">{t('difference')}</Typography>
                <Chip
                  label={getDifference().toLocaleString()}
                  color={Math.abs(getDifference()) > 0.01 ? 'error' : 'success'}
                  variant={Math.abs(getDifference()) > 0.01 ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 800, minWidth: 80 }}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/journal-entries')}
            size="medium"
            color="inherit"
          >
            {isView ? t('BACK') : t('CANCEL')}
          </Button>
          {!isView && (
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading || Math.abs(getDifference()) > 0.01}
              size="medium"
              sx={{ minWidth: 130 }}
            >
              {loading ? t('saving') : t('SAVE')}
            </Button>
          )}
        </Box>
      </form>

      <ImportJournalEntryDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImportSuccess}
      />
    </Box>
  );
};

export default JournalEntryForm;
